/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-children-prop */
import { useState } from 'react';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';

import { LETTER_TYPES, LETTER_TYPE_FIELDS, LETTER_STATUS_FIELDS } from 'utils/typed_constants';
import { setLicensorCall } from 'redux-core/licensors/actions';
import { showGenericError } from 'redux-core/global-error/actions';
import { useTranslation } from 'react-i18next';
import { BaseDrawer, DrawerHeader, TextWithLine } from 'components/styleguide';
import { LetterRow } from './LetterRow';

/* Types for emptyTypeStatusObject
 * type TTypeValues = typeof LETTER_TYPE_FIELDS[keyof typeof LETTER_TYPE_FIELDS]
 * type TStatusValues = typeof LETTER_STATUS_FIELDS[keyof typeof LETTER_STATUS_FIELDS]
 * type TTypeStatusMixValues = `${TTypeValues}${TStatusValues}`;
 */

/**
 * An entry is one date in the drawer. Most are COMPOSED — a letter type plus a status, which
 * is how this module has always named its fields: `license` + `SentAt` = `licenseSentAt`.
 *
 * The three license execution dates are NAMED instead. They do not decompose that way, and
 * they could not become a fourth status either: a status belongs to every letter type, and
 * these three belong only to `License`. So an entry may carry its own field and label. [QW-329]
 */
type TEntry = string | { label: string; field: string };

const entryLabel = (type: string, entry: TEntry) =>
  typeof entry === 'string' ? entry : entry.label;

const entryField = (type: string, entry: TEntry) =>
  typeof entry === 'string' ? LETTER_TYPE_FIELDS[type] + LETTER_STATUS_FIELDS[entry] : entry.field;

/**
 * The drawer's sections, in the order they are shown. Held as data rather than as markup
 * so the dates can be laid out two to a row — and so adding one is an entry here rather
 * than another hand-written row. [QW-345]
 */
const SECTIONS: { heading: string; type: string; entries: TEntry[] }[] = [
  { heading: 'Quote Request', type: LETTER_TYPES.QUOTE_REQUEST, entries: ['Sent', 'Received', 'Expires'] },
  { heading: 'Confirmation', type: LETTER_TYPES.CONFIRMATION, entries: ['Sent'] },
  { heading: 'License Request', type: LETTER_TYPES.LICENSE_REQUEST, entries: ['Sent', 'Received'] },
  {
    heading: 'License',
    type: LETTER_TYPES.LICENSE,
    // The order Qwire's reference shows: Sent, Received, Sent - Internal Signature on the
    // first row, then Partially Executed, Fully Executed, Expires on the second. Expires
    // comes LAST, after the three execution milestones — not before them. [QW-347]
    //
    // The three carry no logic: nothing is computed from them, none validates against
    // another, and no status reads them. [BR-CLDD-3]
    entries: [
      'Sent',
      'Received',
      { label: 'Sent - Internal Signature', field: 'sentInternalSignatureAt' },
      { label: 'Partially Executed', field: 'partiallyExecutedAt' },
      { label: 'Fully Executed', field: 'fullyExecutedAt' },
      'Expires',
    ],
  },
  { heading: 'Check Request', type: LETTER_TYPES.CHECK_REQUEST, entries: ['Sent'] },
];

/** Every field the drawer holds, so a date is empty rather than absent before it is set. */
const EMPTY_DATES = SECTIONS.flatMap(({ type, entries }) => entries.map((e) => entryField(type, e))).reduce(
  (accum, field) => ({ ...accum, [field]: null }),
  {},
);

const PER_ROW = 3;

// Groups a section's entries three to a row, leaving a short row at the end rather than
// borrowing the next section's first — a row never spans two sections.
const inRows = (entries: TEntry[]) =>
  entries.reduce<TEntry[][]>(
    (rows, entry) =>
      (rows.at(-1)?.length ?? PER_ROW) < PER_ROW
        ? [...rows.slice(0, -1), [...rows.at(-1), entry]]
        : [...rows, [entry]],
    [],
  );

type TLettersSetDatesPayload = {
  licensorId: string | number;
  name: string;
  initialDates: any;
  canSendLetters: boolean;
  licensorPath: string;
  setLicensor: (arg0: any) => void;
  refetch?: () => void;
  onSave?: (arg0: any) => void;
  onSubmit?: (arg0: any) => void;
};

type TLicensorLettersSetDatesProps = {
  name: string;
  onClose: () => void;
  payload: TLettersSetDatesPayload;
};

const LettersSetDates = ({
  onClose,
  payload: { licensorId, name, initialDates = EMPTY_DATES, canSendLetters, refetch, onSave, onSubmit },
}: TLicensorLettersSetDatesProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [dates, setDates] = useState(initialDates);

  const onRowDateChange = (row: string) => (value: string) => {
    setDates((prev) => ({ ...prev, [row]: value }));
  };

  const handleOnSave = async () => {
    try {
      if (!onSave) {
        await dispatch(setLicensorCall({ id: licensorId, dates }));
      } else {
        await onSave({ dates });
      }
      refetch?.();
      onSubmit?.(dates);
      onClose();
    } catch (error) {
      // The drawer holds everything in local state, so NOT closing costs nothing — a failed
      // save keeps every typed/picked/cleared date exactly as the user left it. [QW-324]
      dispatch(showGenericError(error));
    }
  };

  const handleOnClose = () => onClose();

  const commonRowProps = {
    canSendLetters,
    onRowDateChange,
    dates,
  };

  return (
    <>
      {/* The Back arrow is removed (not merely conditional): Cancel and Back would do the
          same thing while looking like different offers, and 30 of the other 75 drawers
          already hide it, so there is no house standard being broken. [QW-324] */}
      <DrawerHeader title="Clearance/Licensing Dates" onClose={onClose} hideBackButton testId="project-drawer-header">
        {canSendLetters ? (
          <>
            {/* Cancel sits to the LEFT of Save, as Qwire's reference shows. [QW-347] */}
            <Button variant="outlined" onClick={handleOnClose}>
              {t('global.forms.cancel')}
            </Button>
            <Button variant="contained" onClick={handleOnSave}>
              {t('global.forms.save')}
            </Button>
          </>
        ) : (
          <Button variant="text" onClick={handleOnClose} color="error">
            {t('global.forms.close')}
          </Button>
        )}
      </DrawerHeader>
      <Typography variant="h5" sx={{ marginBottom: '20px' }}>
        {name}
      </Typography>

      <Stack spacing={3}>
        {SECTIONS.map(({ heading, type, entries }) => (
          <Stack spacing={2} key={heading}>
            <TextWithLine children={heading} textPosition="left" />
            {inRows(entries).map((row) => (
              <Grid
                container
                direction="row"
                alignItems="flex-start"
                columnSpacing={3}
                data-testid="letter-row"
                key={row.map((e) => entryField(type, e)).join('-')}
              >
                {row.map((entry) => (
                  <LetterRowWrapper key={entryField(type, entry)} type={type} entry={entry} {...commonRowProps} />
                ))}
              </Grid>
            ))}
          </Stack>
        ))}
      </Stack>
    </>
  );
};

const LetterRowWrapper = ({ type, entry, onRowDateChange, dates, ...rest }) => {
  const field = entryField(type, entry);
  return (
    <LetterRow
      type={type}
      status={entryLabel(type, entry)}
      name={typeof entry === 'string' ? undefined : field}
      onChange={onRowDateChange(field)}
      value={dates[field]}
      {...rest}
    />
  );
};

const Drawer = ({ open, onClose, ...props }) => (
  // @ts-ignore
  <BaseDrawer open={open} onClose={onClose}>
    {/* @ts-ignore */}
    <LettersSetDates onClose={onClose} {...props} />
  </BaseDrawer>
);

export default Drawer;
